<template>
  <CModal
    :show.sync="isVisible"
    :close-on-backdrop="true"
    size="lg"
    centered
    @update:show="handleClose"
  >
    <template #header>
      <h5 class="modal-title">
        <CIcon name="cipInfo" size="sm" class="mr-2"/>
        <translate translate-context="accounting.ledgerExplanation">Chargeback — Calculations</translate>
      </h5>
      <button type="button" class="close" @click="close">
        <span>&times;</span>
      </button>
    </template>

    <div class="ledger-explain">
      <!-- Intro / assumptions -->
      <div class="ledger-explain__intro">
        <div class="intro-row">
          <CIcon name="cipCoin" size="sm" class="mr-2 text-primary"/>
          <span>
            <translate translate-context="accounting.ledgerExplanation">
              A chargeback is a bank-driven reversal of an order, triggered when
            </translate>
            <code>dispute_status = LOST</code>.
            <translate translate-context="accounting.ledgerExplanation">All amounts are in</translate>
            <strong>EUR</strong>
            <translate translate-context="accounting.ledgerExplanation">(Stripe settles disputes in the platform currency).</translate>
          </span>
        </div>
        <div class="intro-row">
          <CIcon name="cipGridSm" size="sm" class="mr-2 text-primary"/>
          <span>
            <translate translate-context="accounting.ledgerExplanation">
              Chargebacks use a dedicated model —
            </translate>
            <code>ChargebackDispute</code> → <code>ChargebackRecord</code> → <code>ChargebackEntry</code>
            <translate translate-context="accounting.ledgerExplanation">
              — parallel to (not inside) the regular ledger.
            </translate>
          </span>
        </div>
        <div class="intro-row text-muted small">
          <CIcon name="cipInfo" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">
            Each line marked with → produces a ChargebackEntry. All entries are negative (losses) — the (−) sign is implied on every one.
          </translate>
        </div>
      </div>

      <!-- Flow overview -->
      <div class="section-title">
        <span class="section-title__badge">for each</span>
        <code class="section-title__target">LedgerLine</code>
        <span class="section-title__note">
          <translate translate-context="accounting.ledgerExplanation">
            of the original order → one ChargebackRecord. Dispute fee allocated only across ORDER_ITEM lines.
          </translate>
        </span>
      </div>

      <!-- ORDER_ITEM line block -->
      <div class="subsection-title">
        <CBadge color="primary" class="mr-2">ORDER_ITEM</CBadge>
        <span><translate translate-context="accounting.ledgerExplanation">line</translate></span>
      </div>

      <!-- Dispute fee allocation -->
      <div class="formula-card formula-card--meta">
        <div class="formula-card__title">
          <CIcon name="cipPercent" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">Dispute fee allocation (proportional to line amount)</translate>
        </div>
        <ul class="formula-list">
          <li>
            <code>items_total_eur</code>
            <span class="eq">=</span>
            <code class="src">Σ line.line_amount_eur</code>
            <span class="note">
              <translate translate-context="accounting.ledgerExplanation">sum over all ORDER_ITEM lines</translate>
            </span>
          </li>
          <li>
            <code>block_fee</code>
            <span class="eq">=</span>
            <code class="src">dispute.dispute_fee_eur × line.line_amount_eur / items_total_eur</code>
          </li>
          <li class="note-only">
            <span class="note">
              <translate translate-context="accounting.ledgerExplanation">the last item block absorbs the cent-level diff vs dispute_fee_eur</translate>
            </span>
          </li>
        </ul>
      </div>

      <!-- Revenue losses -->
      <div class="formula-card formula-card--meta">
        <div class="formula-card__title">
          <CIcon name="cipDollar" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">Revenue losses per stakeholder (net of prior refunds)</translate>
        </div>
        <div class="mini-formula">
          <code>remaining_revenue[stakeholder]</code>
          <span class="eq">=</span>
          <code class="src">Σ original_revenue_entries + Σ refund_revenue_entries</code>
        </div>
      </div>

      <FormulaBlock
        name="remaining_revenue[Photographer]"
        formula="Σ photographer original revenue + refund revenue"
        :produces="[{ stakeholder: 'photographer', entryType: 'REVENUE', negative: true }]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">emitted only when > 0</translate>
        </template>
      </FormulaBlock>

      <FormulaBlock
        name="remaining_revenue[Pica]"
        formula="Σ pica original revenue + refund revenue"
        :produces="[{ stakeholder: 'pica', entryType: 'REVENUE', negative: true }]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">emitted only when > 0</translate>
        </template>
      </FormulaBlock>

      <FormulaBlock
        name="remaining_revenue[Organizer]"
        formula="Σ organizer original revenue + refund revenue"
        :produces="[{ stakeholder: 'organizer', entryType: 'REVENUE', negative: true }]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">emitted only when > 0</translate>
        </template>
      </FormulaBlock>

      <FormulaBlock
        name="remaining_revenue[Agent]"
        formula="Σ agent original revenue + refund revenue"
        :produces="[{ stakeholder: 'agent', entryType: 'REVENUE', negative: true }]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">emitted only when > 0</translate>
        </template>
      </FormulaBlock>

      <!-- VAT reversal -->
      <FormulaBlock
        name="vat_amount"
        formula="|Σ original_vat_entries[line].amount_eur|"
        :produces="[{ stakeholder: 'pica', entryType: 'VAT', negative: true }]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">
            uses raw original VAT (NOT net of prior refund VAT — see notes). Emitted only when > 0.
          </translate>
        </template>
      </FormulaBlock>

      <!-- Dispute fee split -->
      <div class="formula-card formula-card--meta">
        <div class="formula-card__title">
          <CIcon name="cipPercent" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">Dispute fee split — same percentages as the original item</translate>
        </div>
        <div class="mini-formula">
          <code>photographer_percentage</code>
          <span class="eq">=</span>
          <code class="src">100 − pica_fee_percentage − organizer_fee_percentage</code>
        </div>
      </div>

      <FormulaBlock
        name="photo_dispute_fee"
        formula="block_fee × photographer_percentage / 100"
        :produces="[{ stakeholder: 'photographer', entryType: 'DISPUTE_FEE', negative: true }]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">only if photographer_percentage > 0</translate>
        </template>
      </FormulaBlock>

      <FormulaBlock
        name="org_dispute_fee"
        formula="block_fee × organizer_fee_percentage / 100"
        :produces="[{ stakeholder: 'organizer', entryType: 'DISPUTE_FEE', negative: true }]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">only if organizer_fee_percentage > 0</translate>
        </template>
      </FormulaBlock>

      <FormulaBlock
        name="pica_dispute_fee"
        formula="block_fee − photo_dispute_fee − org_dispute_fee"
        :produces="[{ stakeholder: 'pica', entryType: 'DISPUTE_FEE', negative: true }]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">absorbs rounding on the split</translate>
        </template>
      </FormulaBlock>

      <!-- Record totals -->
      <div class="totals-card">
        <div class="totals-card__title">
          <CIcon name="cipCalculator" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">Record totals (ORDER_ITEM)</translate>
        </div>
        <div class="totals-card__row">
          <code>record.chargeback_amount</code>
          <span class="eq">=</span>
          <code class="src">Σ remaining_revenue[*] + vat_amount</code>
        </div>
        <div class="totals-card__row">
          <code>record.dispute_fee_amount</code>
          <span class="eq">=</span>
          <code class="src">block_fee</code>
        </div>
      </div>

      <!-- HANDLING_FEE line block -->
      <div class="subsection-title mt-4">
        <CBadge color="info" class="mr-2">HANDLING_FEE</CBadge>
        <span>
          <translate translate-context="accounting.ledgerExplanation">line — no dispute-fee share, reversed in full</translate>
        </span>
      </div>

      <FormulaBlock
        name="handling_amount"
        formula="line.line_amount_eur"
        :produces="[{ stakeholder: 'pica', entryType: 'HANDLING_FEE', negative: true }]"
      />

      <div class="totals-card">
        <div class="totals-card__title">
          <CIcon name="cipCalculator" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">Record totals (HANDLING_FEE)</translate>
        </div>
        <div class="totals-card__row">
          <code>record.chargeback_amount</code>
          <span class="eq">=</span>
          <code class="src">handling_amount</code>
        </div>
        <div class="totals-card__row">
          <code>record.dispute_fee_amount</code>
          <span class="eq">=</span>
          <code class="src">0</code>
        </div>
      </div>

      <!-- API exposed figures -->
      <div class="section-title mt-4">
        <span class="section-title__badge">API</span>
        <span class="section-title__note">
          <translate translate-context="accounting.ledgerExplanation">Exposed figures — per-record and per-dispute</translate>
        </span>
      </div>

      <div class="api-figures-grid">
        <div class="api-figures-card">
          <div class="api-figures-card__title">
            <CIcon name="cipLayers" size="sm" class="mr-2"/>
            <translate translate-context="accounting.ledgerExplanation">Per-record (current block)</translate>
          </div>
          <ul class="api-figures-list">
            <li><code>chargeback_amount</code> — <translate translate-context="accounting.ledgerExplanation">revenues + VAT being reversed</translate></li>
            <li><code>dispute_fee</code> — <translate translate-context="accounting.ledgerExplanation">allocated block_fee (0 for handling)</translate></li>
            <li><code>total_loss</code> — <code>chargeback_amount + dispute_fee</code></li>
            <li><code>photographer_loss</code>, <code>pica_loss</code>, <code>organizer_loss</code></li>
          </ul>
        </div>
        <div class="api-figures-card">
          <div class="api-figures-card__title">
            <CIcon name="cipMoney" size="sm" class="mr-2"/>
            <translate translate-context="accounting.ledgerExplanation">Per-dispute (aggregates)</translate>
          </div>
          <ul class="api-figures-list">
            <li><code>records_count</code></li>
            <li><code>total_chargeback_amount</code>, <code>total_dispute_fee</code>, <code>total_loss</code></li>
            <li><code>photographer_loss</code>, <code>pica_loss</code>, <code>organizer_loss</code>
              <translate translate-context="accounting.ledgerExplanation">(summed across records)</translate>
            </li>
          </ul>
        </div>
      </div>

      <!-- Worked example -->
      <div class="section-title mt-4">
        <span class="section-title__badge section-title__badge--alt">example</span>
        <span class="section-title__note">
          <translate translate-context="accounting.ledgerExplanation">
            One item — pica_fee 10%, organizer_fee 5%, agent 5%, handling > 0, no prior refunds. dispute_fee_eur = 15.00
          </translate>
        </span>
      </div>

      <div class="example-card">
        <div class="example-card__title">
          <translate translate-context="accounting.ledgerExplanation">Original order entries (EUR)</translate>
        </div>
        <pre class="code-block">Photographer  REVENUE       = +81.40
Pica          REVENUE       = +11.00   (pica_fee)
Pica          REVENUE       =  −0.55   (agent offset)
Organizer     REVENUE       =  +5.50
Agent         REVENUE       =  +0.55
Pica          VAT           = −12.10
Pica          HANDLING_FEE  =  +5.50</pre>

        <div class="example-card__title mt-3">
          <translate translate-context="accounting.ledgerExplanation">Item record entries</translate>
        </div>
        <pre class="code-block">remaining_revenue.photographer = 81.40      → Photographer REVENUE      = 81.40
remaining_revenue.pica         = 10.45      → Pica         REVENUE      = 10.45
remaining_revenue.organizer    =  5.50      → Organizer    REVENUE      =  5.50
remaining_revenue.agent        =  0.55      → Agent        REVENUE      =  0.55
vat_amount                     = 12.10      → Pica         VAT          = 12.10

photo_dispute_fee = 15 × 85/100 = 12.75     → Photographer DISPUTE_FEE  = 12.75
org_dispute_fee   = 15 ×  5/100 =  0.75     → Organizer    DISPUTE_FEE  =  0.75
pica_dispute_fee  = 15 − 12.75 − 0.75 = 1.50 → Pica        DISPUTE_FEE  =  1.50

record.chargeback_amount  = 110.00
record.dispute_fee       =  15.00
record.total_loss        = 125.00</pre>

        <div class="example-card__title mt-3">
          <translate translate-context="accounting.ledgerExplanation">Stakeholder breakdown (record)</translate>
        </div>
        <pre class="code-block">photographer_loss = 81.40 + 12.75 = 94.15
pica_loss         = 10.45 + 12.10 +  1.50 = 24.05
organizer_loss    =  5.50 +  0.75 =  6.25
                                   ─────────
                                     124.45
(agent 0.55 not bucketed — see open questions)</pre>

        <div class="example-card__title mt-3">
          <translate translate-context="accounting.ledgerExplanation">Handling record entries</translate>
        </div>
        <pre class="code-block">handling_amount = 5.50                       → Pica HANDLING_FEE = 5.50

record.chargeback_amount  = 5.50
record.dispute_fee        = 0.00
record.total_loss         = 5.50</pre>
      </div>

      <!-- Divergence caveat -->
      <div class="caveat-card mt-4">
        <div class="caveat-card__title">
          <CIcon name="cipAlertTriangle" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">
            Dispute totals vs stakeholder breakdown — possible divergence
          </translate>
        </div>
        <p class="caveat-card__body">
          <code>photographer_loss + pica_loss + organizer_loss == total_loss</code>
          <translate translate-context="accounting.ledgerExplanation">
            holds at the record level only when no agent is involved; at the dispute level it's equivalent up to rounding across records.
          </translate>
        </p>
      </div>

      <!-- Open questions -->
      <div class="section-title mt-4">
        <span class="section-title__badge section-title__badge--warn">open questions</span>
        <span class="section-title__note">
          <translate translate-context="accounting.ledgerExplanation">Tracked inconsistencies — to revisit</translate>
        </span>
      </div>

      <ul class="open-questions__list">
        <li>
          <strong><translate translate-context="accounting.ledgerExplanation">AGENT missing in the loss breakdown</translate></strong> —
          <translate translate-context="accounting.ledgerExplanation">
            the serializer ignores StakeholderType.AGENT entries. For orders with an agent configured, total_loss over-counts by the agent share. Options:
          </translate>
          <ul>
            <li><translate translate-context="accounting.ledgerExplanation">add agent_loss as a fourth bucket (breaking change);</translate></li>
            <li><translate translate-context="accounting.ledgerExplanation">fold AGENT into photographer_loss (agent fees come out of the photographer side);</translate></li>
            <li><translate translate-context="accounting.ledgerExplanation">leave as-is and rely on agents being rare / negligible amounts.</translate></li>
          </ul>
        </li>
        <li>
          <strong><translate translate-context="accounting.ledgerExplanation">Handling-fee chargeback block has no VAT split</translate></strong> —
          <translate translate-context="accounting.ledgerExplanation">
            _build_handling_fee_block emits a single HANDLING_FEE = −gross entry. After the HandlingFeeEntryBuilder fix the ledger splits handling into HANDLING_FEE_net + VAT, but the chargeback reverses the gross as one entry. Totals match, but per-entry-type aggregation (ledger VAT vs chargeback VAT) is inconsistent.
          </translate>
        </li>
        <li>
          <strong><translate translate-context="accounting.ledgerExplanation">Prior refunds and VAT</translate></strong> —
          <translate translate-context="accounting.ledgerExplanation">
            revenue losses net out prior refunds; VAT loss does not (see Notes).
          </translate>
        </li>
      </ul>

      <!-- Notes -->
      <div class="notes">
        <div class="notes__title">
          <CIcon name="cipInfo" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">Notes</translate>
        </div>
        <ul class="notes__list">
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">EUR only</translate></strong>:
            <code>amount</code> <translate translate-context="accounting.ledgerExplanation">and</translate> <code>amount_eur</code>
            <translate translate-context="accounting.ledgerExplanation">
              coincide on every ChargebackEntry — no currency split.
            </translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">Triggered by LOST only</translate></strong>:
            <translate translate-context="accounting.ledgerExplanation">
              won / needs-response disputes leave no records or entries.
            </translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">Prior refunds</translate></strong>:
            <translate translate-context="accounting.ledgerExplanation">
              revenue losses are net of prior refunds on the same order item. The VAT loss is NOT netted — tracked inconsistency.
            </translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">Fully refunded items</translate></strong>:
            <code>remaining_revenue[*]</code>
            <translate translate-context="accounting.ledgerExplanation">
              can all be 0, but the item block still carries its block_fee. Stripe takes the dispute fee regardless.
            </translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">Agents are never charged</translate></strong>
            <translate translate-context="accounting.ledgerExplanation">for the dispute fee — they only lose their revenue.</translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">Rounding</translate></strong>:
            <translate translate-context="accounting.ledgerExplanation">
              each block's pica_dispute_fee absorbs its own split residue; the last item block additionally absorbs the cent diff vs the total dispute_fee_eur.
            </translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">Reprocessing</translate></strong>:
            <code>manage.py populate_ledger</code>
            <translate translate-context="accounting.ledgerExplanation">→ Regenerate by ID → Chargeback Record ID deletes records (entries cascade) and rebuilds from scratch.</translate>
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <CButton color="secondary" variant="outline" @click="close">
        <translate translate-context="accounting.ledgerExplanation">Close</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import FormulaBlock from '@/domain/accounting/order/components/FormulaBlock.vue'

export default {
  name: 'ChargebackLedgerExplanationModal',
  components: {FormulaBlock},
  data() {
    return {
      isVisible: false
    }
  },
  methods: {
    open() {
      this.isVisible = true
    },
    close() {
      this.isVisible = false
    },
    handleClose(val) {
      if (!val) this.close()
    }
  }
}
</script>

<style scoped>
.ledger-explain {
  padding: 4px 8px 16px;
}

.ledger-explain__intro {
  background: #f7f8fb;
  border: 1px solid #e4e7ea;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 18px;
}

.intro-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  line-height: 1.4;
}

.intro-row:last-child {
  margin-bottom: 0;
}

.intro-row code {
  background: #eef1f5;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.82rem;
}

.section-title {
  display: flex;
  align-items: center;
  margin: 22px 0 10px;
  gap: 8px;
  flex-wrap: wrap;
}

.section-title__badge {
  background: #100F23;
  color: #fff;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 3px;
  font-weight: 600;
}

.section-title__badge--alt {
  background: #1867FF;
}

.section-title__badge--warn {
  background: #b8791a;
}

.api-figures-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 4px;
}

@media (max-width: 576px) {
  .api-figures-grid {
    grid-template-columns: 1fr;
  }
}

.api-figures-card {
  background: #fafbfd;
  border: 1px solid #e4e7ea;
  border-radius: 6px;
  padding: 10px 12px;
}

.api-figures-card__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.88rem;
  color: #100F23;
  margin-bottom: 6px;
}

.api-figures-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.82rem;
  color: #3c3e4a;
}

.api-figures-list li {
  margin-bottom: 4px;
  line-height: 1.4;
}

.api-figures-list code {
  background: #fff;
  border: 1px solid #e4e7ea;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.8rem;
}

.caveat-card {
  background: #fff6ec;
  border: 1px solid #f3d3a8;
  border-radius: 6px;
  padding: 10px 14px;
}

.caveat-card__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #8a5a12;
  margin-bottom: 6px;
}

.caveat-card__body {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.45;
  color: #3c3a2a;
}

.caveat-card__body code {
  background: #fdf3d7;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.82rem;
}

.open-questions__list {
  margin: 6px 0 0;
  padding-left: 20px;
  color: #3c3e4a;
  font-size: 0.88rem;
}

.open-questions__list > li {
  margin-bottom: 10px;
  line-height: 1.45;
}

.open-questions__list > li:last-child {
  margin-bottom: 0;
}

.open-questions__list ul {
  margin-top: 4px;
  padding-left: 18px;
  list-style: disc;
  font-size: 0.84rem;
  color: #5a5d6b;
}

.section-title__target {
  background: #eef1f5;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 600;
}

.section-title__note {
  color: #6c7285;
  font-size: 0.9rem;
}

.subsection-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin: 12px 0 10px;
}

.formula-card--meta {
  background: #fafbfd;
  border: 1px dashed #c8cdd6;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 12px;
}

.formula-card__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 8px;
  color: #100F23;
}

.formula-list,
.mini-formula {
  margin: 0;
  padding: 0;
  list-style: none;
}

.formula-list li,
.mini-formula {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0;
  font-size: 0.9rem;
}

.formula-list code,
.mini-formula code {
  background: #fff;
  border: 1px solid #e4e7ea;
  padding: 1px 6px;
  border-radius: 3px;
}

.formula-list .src,
.mini-formula .src {
  background: #eef1f5;
  border: none;
}

.formula-list .eq,
.mini-formula .eq {
  color: #6c7285;
  font-weight: 600;
}

.formula-list .note {
  color: #6c7285;
  font-size: 0.78rem;
  font-style: italic;
}

.formula-list .note-only {
  padding-left: 8px;
}

.totals-card {
  background: #f1f6ff;
  border: 1px solid #c5d9ff;
  border-radius: 6px;
  padding: 10px 14px;
  margin: 10px 0 6px;
}

.totals-card__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 6px;
  color: #10378a;
  font-size: 0.9rem;
}

.totals-card__row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 3px 0;
  font-size: 0.88rem;
}

.totals-card__row code {
  background: #fff;
  border: 1px solid #c5d9ff;
  padding: 1px 6px;
  border-radius: 3px;
}

.totals-card__row .src {
  background: #e0ecff;
  border: none;
}

.totals-card__row .eq {
  color: #6c7285;
  font-weight: 600;
}

.example-card {
  background: #fafbfd;
  border: 1px solid #e4e7ea;
  border-radius: 6px;
  padding: 12px 14px;
  margin-top: 8px;
}

.example-card__title {
  font-weight: 600;
  font-size: 0.85rem;
  color: #100F23;
  margin-bottom: 4px;
}

.code-block {
  background: #f4f6f9;
  border: 1px solid #e4e7ea;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 0.78rem;
  line-height: 1.35;
  color: #2c2f3a;
  white-space: pre;
  overflow-x: auto;
  margin: 0;
}

.notes {
  margin-top: 22px;
  background: #fffaf0;
  border: 1px solid #f3dfb8;
  border-radius: 6px;
  padding: 12px 14px;
}

.notes__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #7a5a12;
  margin-bottom: 8px;
}

.notes__list {
  margin: 0;
  padding-left: 18px;
  color: #3c3a2a;
}

.notes__list li {
  margin-bottom: 6px;
  line-height: 1.45;
  font-size: 0.88rem;
}

.notes__list li:last-child {
  margin-bottom: 0;
}

.notes__list code {
  background: #fdf3d7;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.82rem;
}
</style>
